"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import axios from "axios"
import { API_URL } from "../config"
import { useLanguage } from "../context/LanguageContext"
import { useNotification } from "../context/NotificationContext"
import { APIProvider, Map, Marker } from "@vis.gl/react-google-maps"

import "./AddFarm.css"

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "AIzaSyCT_w1O7dUdXlSvWCRn-DxU8e11Vd0t94k" // or your method
const DEFAULT_CENTER = { lat: 9.145, lng: 40.4897 } // Ethiopia center as example

const AddFarm = () => {
  const navigate = useNavigate()
  const { t, direction } = useLanguage()
  const { showNotification } = useNotification()

  const [formData, setFormData] = useState({
    name: "",
    code: "",
    country: "",
    district: "",
    location: {
      coordinates: [0, 0],
    },
    area: "",
    length: "",
    width: "",
    parameterGroup: "",
  })

  const [countries, setCountries] = useState([])
  const [districts, setDistricts] = useState([])
  const [parameterGroups, setParameterGroups] = useState([])
  const [message, setMessage] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [areaCalculationMethod, setAreaCalculationMethod] = useState("direct") // "direct" or "dimensions"

  // Map state
  const [mapCenter, setMapCenter] = useState({
    lat: formData.location.coordinates[1] || DEFAULT_CENTER.lat,
    lng: formData.location.coordinates[0] || DEFAULT_CENTER.lng,
  })
  const [mapZoom, setMapZoom] = useState(6)
  const [markerCoords, setMarkerCoords] = useState(
    formData.location.coordinates[0] !== 0 || formData.location.coordinates[1] !== 0
      ? { lat: formData.location.coordinates[1], lng: formData.location.coordinates[0] }
      : null
  )
  const [countryText, setCountryText] = useState("")
  const [districtText, setDistrictText] = useState("")

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)

        // Fetch countries
        const countriesRes = await axios.get(`${API_URL}/api/locations/countries`)
        setCountries(countriesRes.data)

        // Fetch parameter groups
        const groupsRes = await axios.get(`${API_URL}/api/groups`)
        setParameterGroups(groupsRes.data)

        setLoading(false)
      } catch (err) {
        setError("Failed to load reference data")
        setLoading(false)
        console.error(err)
      }
    }

    fetchData()
  }, [])

  // Fetch districts when country changes
  useEffect(() => {
    if (!formData.country) {
      setDistricts([])
      return
    }

    const fetchDistricts = async () => {
      // try {
      //   const res = await axios.get(`${API_URL}/api/locations/districts/${formData.country}`)
      //   setDistricts(res.data)
      //
      //   console.log("Fetched districts ds", districts)
      // } catch (err) {
      //   setError("Failed to fetch districts")
      //   console.error(err)
      // }
    }

    fetchDistricts()
  }, [formData.country])

  // Update marker and formData when coordinates change from input
  useEffect(() => {
    if (
      formData.location.coordinates[0] !== 0 ||
      formData.location.coordinates[1] !== 0
    ) {
      setMarkerCoords({
        lat: formData.location.coordinates[1],
        lng: formData.location.coordinates[0],
      })
      setMapCenter({
        lat: formData.location.coordinates[1],
        lng: formData.location.coordinates[0],
      })
    }
  }, [formData.location.coordinates])


  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
          async (position) => {
            const lat = position.coords.latitude
            const lng = position.coords.longitude
            setMapCenter({ lat, lng })
            setMarkerCoords({ lat, lng })
            setFormData((prev) => ({
              ...prev,
              location: {
                ...prev.location,
                coordinates: [lng, lat],
              },
            }))

            // Reverse geocode current location
            try {
              const res = await axios.get(`${API_URL}/api/reverse-geocode`, {
                params: { lat, lon: lng },
              })
              if (res.data.results?.length > 0) {
                const result = res.data.results[0]
                let countryName = ""
                let districtName = ""
                if (result.address_components) {
                  const countryComp = result.address_components.find((c) =>
                      c.types.includes("country")
                  )
                  const districtComp = result.address_components.find((c) =>
                      c.types.includes("administrative_area_level_2")
                  )
                  if (countryComp) countryName = countryComp.long_name
                  if (districtComp) districtName = districtComp.long_name
                }
                setCountryText(countryName)
                setDistrictText(districtName)
                setFormData((prev) => ({
                  ...prev,
                  country: countryName,
                  district: districtName,
                }))
              }
            } catch (err) {
              setCountryText("")
              setDistrictText("")
            }
          },
          (error) => {
            setMapCenter(DEFAULT_CENTER)
          }
      )
    } else {
      setMapCenter(DEFAULT_CENTER)
    }
  }, [])

  // Handle map click
const handleMapClick = async (e) => {
  const lat = e.detail.latLng.lat
  const lng = e.detail.latLng.lng
  setMarkerCoords({ lat, lng })
  setMapCenter({ lat, lng })

  setFormData((prev) => ({
    ...prev,
    location: {
      ...prev.location,
      coordinates: [lng, lat],
    },
  }))

  try {
    console.log("debug: reverse geocode called", lat, lng)
    const res = await axios.get(`${API_URL}/api/reverse-geocode`, {
      params: { lat, lon : lng },
    })
    if (res.data.results?.length > 0) {
      const result = res.data.results[0]
      let countryName = ""
      let districtName = ""
      if (result.address_components) {
        const countryComp = result.address_components.find((c) =>
            c.types.includes("country")
        )
        const districtComp = result.address_components.find((c) =>
            c.types.includes("administrative_area_level_2")
        )
        if (countryComp) countryName = countryComp.long_name
        if (districtComp) districtName = districtComp.long_name
      }
      setCountryText(countryName)
      setDistrictText(districtName)
      setFormData((prev) => ({
        ...prev,
        country: countryName,
        district: districtName,
      }))
    }
  } catch (err) {
    setCountryText("")
    setDistrictText("")
  }
}

  const handleChange = (e) => {
    const { name, value } = e.target

    if (name === "country") {
      // Reset district when country changes
      setFormData({
        ...formData,
        country: value,
        district: "",
      })
    } else if (name === "length" || name === "width") {
      // Update the value
      setFormData({
        ...formData,
        [name]: value,
      })

      console.log("debug: handleChange called")
      console.log("Field changed:", name, "Value:", value)

      // Calculate area if both length and width are provided
      if (areaCalculationMethod === "dimensions") {
        const length = name === "length" ? value : formData.length
        const width = name === "width" ? value : formData.width

        if (length && width) {
          const area = Number.parseFloat(length) * Number.parseFloat(width)
          setFormData((prev) => ({
            ...prev,
            area: area.toFixed(2),
          }))
        }
      }
    } else if (name === "district") {
      // Reset location coordinates when district changes

      // filter districts to find the selected one

      const selectedDistrict = districts.find((district) => district._id === value)
      if (selectedDistrict) {
        console.log("Selected district:", selectedDistrict)
        setFormData({
          ...formData,
          district: value,
          location: {
            coordinates: selectedDistrict.location.coordinates || [0, 0], // Use district coordinates if available
          },
        })

        // // change the coordinates in the input fields
        // document.getElementById("longitude").value = selectedDistrict.location.coordinates[0] || 0;
        // document.getElementById("latitude").value = selectedDistrict.location.coordinates[1] || 0;

        console.log("Selected district details:", selectedDistrict)
      }

      console.log("District changed:", value)
    } else {
      setFormData({
        ...formData,
        [name]: value,
      })
    }

    // Clear any previous messages
    setMessage(null)
    setError(null)
  }

  const handleCoordinateChange = (index, value) => {
    console.log("debug: handleCoordinateChange called")
    console.log("Coordinate change", index, value)
    const coordinates = [...formData.location.coordinates]
    coordinates[index] = Number.parseFloat(value)

    setFormData({
      ...formData,
      location: {
        ...formData.location,
        coordinates,
      },
    })
  }

  const generateFarmCode = () => {
    // Generate a unique farm code (simple example)
    const dateCode = new Date().getTime().toString().slice(-6)
    const nameCode = formData.name.slice(0, 3).toUpperCase()

    const farmCode = `${nameCode}-${dateCode}`

    setFormData({
      ...formData,
      code: farmCode,
    })
  }

  const handleAreaMethodChange = (method) => {
    setAreaCalculationMethod(method)

    // Reset area fields when changing method
    setFormData({
      ...formData,
      area: "",
      length: "",
      width: "",
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    try {
      setLoading(true)

      // Prepare data for submission
      const farmData = {
        ...formData,
        area: Number.parseFloat(formData.area),
      }

      // Add length and width if using dimensions method
      if (areaCalculationMethod === "dimensions") {
        farmData.length = Number.parseFloat(formData.length)
        farmData.width = Number.parseFloat(formData.width)
      }

      const response = await axios.post(`${API_URL}/api/farms`, farmData)

      if (response.data) {
        setMessage("Farm added successfully")
        showNotification("Farm added successfully", "success")
        // Navigate to dashboard after 2 seconds
        setTimeout(() => {
          navigate("/dashboard")
        }, 2000)
      }
    } catch (err) {
      setError(err.response?.data?.msg || "Failed to add farm")
      showNotification(err.response?.data?.msg || "Failed to add farm", "error")
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  if (loading && countries.length === 0) {
    return (
        <div className="farm-loading">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
    )
  }

  return (
      <div className="add-farm-container" dir={direction}>
        <div className="add-farm-header">
          <h1>{t("Add New Farm")}</h1>
          <p className="add-farm-subtitle">{t("Enter your farm details to add it to your account")}</p>
        </div>

        {message && (
            <div className="alert-success farm-alert">
              <i className="fas fa-check-circle"></i>
              {message}
            </div>
        )}

        {error && (
            <div className="alert-error farm-alert">
              <i className="fas fa-exclamation-circle"></i>
              {error}
            </div>
        )}

        <div className="add-farm-card">
          <form onSubmit={handleSubmit} className="farm-form">
            <div className="farm-section">
              <h2 className="section-title">{t("Basic Information")}</h2>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">{t("Farm Name")}</label>
                  <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="form-control"
                      required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="code">{t("Farm Code")}</label>
                  <div className="input-with-button">
                    <input
                        type="text"
                        id="code"
                        name="code"
                        value={formData.code}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="Leave empty to auto-generate"
                    />
                    <button type="button" className="generate-button" onClick={generateFarmCode}>
                      <i className="fas fa-sync-alt"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="farm-section">
              <h2 className="section-title">{t("Location")}</h2>

              {/* Map Section */}
              <div className="map-section" style={{ marginBottom: 24 }}>
                <div className="map-container" style={{ width: "100%", height: 300, borderRadius: 8, overflow: "hidden" }}>
                  <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                    <Map
                      center={mapCenter}
                      zoom={mapZoom}
                      gestureHandling="greedy"
                      disableDefaultUI={false}
                      zoomControl={true}
                      mapTypeControl={true}
                      streetViewControl={true}
                      fullscreenControl={true}
                      style={{ width: "100%", height: "100%" }}
                      onClick={handleMapClick}
                      onZoomChanged={(e) => setMapZoom(e.detail.zoom)}
                      onCenterChanged={(e) => {
                        const center = e.detail.center
                        setMapCenter({ lat: center.lat, lng: center.lng })
                      }}
                    >
                      {markerCoords && <Marker position={markerCoords} />}
                    </Map>
                  </APIProvider>
                </div>
                <small style={{ color: "#888" }}>
                  Click on the map to set farm location. Country and district will be auto-filled if possible.
                </small>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="country">{t("Country")}</label>
                  <input
                    type="text"
                    id="country"
                    name="countryText"
                    value={countryText}
                    className="form-control"
                    readOnly
                  />
                </div>
                {/*<div className="form-group">*/}
                {/*  <label htmlFor="district">{t("District")}</label>*/}
                {/*  <input*/}
                {/*    type="text"*/}
                {/*    id="district"*/}
                {/*    name="districtText"*/}
                {/*    value={districtText}*/}
                {/*    className="form-control"*/}
                {/*    readOnly*/}
                {/*  />*/}
                {/*</div>*/}
              </div>

              <div className="form-group">
                <label>{t("Coordinates")}</label>
                <div className="coordinates-inputs">
                  <div className="coordinate-input">
                    <label htmlFor="longitude">{t("Longitude")}</label>
                    <input
                        type="number"
                        id="longitude"
                        value={formData.location.coordinates[0]}
                        onChange={(e) => handleCoordinateChange(0, e.target.value)}
                        className="form-control"
                        step="0.000001"
                    />
                  </div>
                  <div className="coordinate-input">
                    <label htmlFor="latitude">{t("Latitude")}</label>
                    <input
                        type="number"
                        id="latitude"
                        value={formData.location.coordinates[1]}
                        onChange={(e) => handleCoordinateChange(1, e.target.value)}
                        className="form-control"
                        step="0.000001"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* <div className="farm-section">
              <h2 className="section-title">{t("Weather Parameters")}</h2>

              <div className="form-group">
                <label htmlFor="parameterGroup">{t("Parameter Group")}</label>
                <select
                    id="parameterGroup"
                    name="parameterGroup"
                    value={formData.parameterGroup}
                    onChange={handleChange}
                    className="form-control"
                >
                  <option value="">{t("Select a parameter group (optional)")}</option>
                  {parameterGroups.map((group) => (
                      <option key={group._id} value={group._id}>
                        {group.name} - {group.groupId}
                      </option>
                  ))}
                </select>
                <small className="form-text">
                  {t("Select a parameter group to use for weather data calculations for this farm")}
                </small>
              </div>
            </div> */}

            <div className="farm-section">
              <h2 className="section-title">{t("Farm Area")}</h2>

              <div className="area-calculation-toggle">
                <button
                    type="button"
                    className={`toggle-btn ${areaCalculationMethod === "direct" ? "active" : ""}`}
                    onClick={() => handleAreaMethodChange("direct")}
                >
                  {t("Enter Area Directly")}
                </button>
                <button
                    type="button"
                    className={`toggle-btn ${areaCalculationMethod === "dimensions" ? "active" : ""}`}
                    onClick={() => handleAreaMethodChange("dimensions")}
                >
                  {t("Calculate from Length × Width")}
                </button>
              </div>

              {areaCalculationMethod === "direct" ? (
                  <div className="form-group">
                    <label htmlFor="area">{t("Area (square meters)")}</label>
                    <input
                        type="number"
                        id="area"
                        name="area"
                        value={formData.area}
                        onChange={handleChange}
                        className="form-control"
                        min="0"
                        step="0.01"
                        required
                    />
                  </div>
              ) : (
                  <div className="dimensions-inputs">
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="length">{t("Length (meters)")}</label>
                        <input
                            type="number"
                            id="length"
                            name="length"
                            value={formData.length}
                            onChange={handleChange}
                            className="form-control"
                            min="0"
                            step="0.01"
                            required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="width">{t("Width (meters)")}</label>
                        <input
                            type="number"
                            id="width"
                            name="width"
                            value={formData.width}
                            onChange={handleChange}
                            className="form-control"
                            min="0"
                            step="0.01"
                            required
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label htmlFor="calculated-area">{t("Calculated Area (square meters)")}</label>
                      <input
                          type="text"
                          id="calculated-area"
                          value={formData.area}
                          className="form-control"
                          readOnly
                          disabled
                      />
                    </div>
                  </div>
              )}
            </div>

            <div className="form-actions">
              <button type="button" className="cancel-button" onClick={() => navigate("/dashboard")}>
                {t("Cancel")}
              </button>
              <button type="submit" className="submit-button" disabled={loading}>
                {loading ? t("Adding...") : t("Add Farm")}
              </button>
            </div>
          </form>
        </div>
      </div>
  )
}

export default AddFarm
